import React, { useState } from 'react';
import { matchSorter } from 'match-sorter';
import { Link } from 'react-router-dom';
import pluralize from 'pluralize';
import {
  Text,
  Stack,
  Button,
  Modal,
  ButtonGroup,
  Input,
  Menu,
  Card,
  TableSort,
  TextLink,
  usePagination,
  Table,
  Pagination,
  Shelf,
} from '@chaser/components';
import useFetch from '../../hooks/useFetch';
import AdminService from '../../services/admin-service';
import usePush from '../../hooks/usePush';
import AccountService from '../../services/account-service';
import useTableState, { Order } from '../../hooks/useTableState';
import { Form, Formik } from 'formik';
import FieldWithErrorLabel from '../../components/FieldWithErrorLabel';
import OptionsMenu from '../../components/OptionsMenu';
import useModal from '../../hooks/useModal';
import SettingsService from '../../services/settings-service';
import ChaserSelect from '../../components/ChaserSelect';

function sort(data, orderedColumn, ordering) {
  if (ordering === Order.NONE) return data;

  return data.sort((o1, o2) => {
    let comparison;
    if (orderedColumn === 1) {
      const o1Name = o1.name || '';
      const o2Name = o2.name || '';
      comparison = o1Name.localeCompare(o2Name);
    }
    if (orderedColumn === 2) {
      comparison = o1.accountingSoftware.localeCompare(o2.accountingSoftware);
    } else {
      comparison = +(o2.joined || 0) - (o1.joined || 0);
    }
    const i = ordering === Order.ASC ? 1 : -1;
    if (comparison > 0) {
      return i;
    }
    if (comparison < 0) {
      return -i;
    }
    return 0;
  });
}

const Organisations = () => {
  const { data, refetch: refetchAllOrgs } = useFetch(AdminService.getOrganisations);
  const { data: userOrgs, refetch: refetchUserOrgs } = useFetch(
    AccountService.getUserOrganisations,
  );
  const [{ search, sortProp, ordering, page }, dispatch] = useTableState({ searchProp: 1 });
  const showModal = useModal();

  function onSort(prop) {
    dispatch({ type: 'TOGGLE_ORDERING', payload: prop });
  }

  const merged = data.map(org => {
    const userOrg = userOrgs.find(o => o.ShortCode === org.ShortCode);
    if (!userOrg) return org;
    return {
      ...org,
      joined: true,
    };
  });

  const filtered = matchSorter(merged, search, {
    keys: ['name', 'ShortCode', 'accountingSoftware', 'subscription.planOffline.name'],
  });

  const organisations = sort(filtered, sortProp, ordering);

  const currentSort =
    ordering !== Order.NONE ? `${ordering === Order.DESC ? '-' : ''}${sortProp}` : '';

  const columns = [
    {
      accessor: 'name',
      cell: (name, org) => (
        <TextLink trim={false} as={Link} to={`/o/${org.ShortCode}`}>
          {name || org.Name}
        </TextLink>
      ),
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp={1}
          onSort={onSort}
          headerTitle="Organisation name"
        />
      ),
    },
    {
      accessor: 'accountingSoftware',
      cell: accountingSoftware => <Text>{accountingSoftware}</Text>,
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp={2}
          onSort={onSort}
          headerTitle="Integration"
        />
      ),
    },
    {
      accessor: 'subscription.planOffline.name',
      header: 'Plan',
      cell: plan => <Text>{plan}</Text>,
    },
    {
      accessor: 'ShortCode',
      align: 'right',
      headerStyle: { width: '150px' },
      cell: (ShortCode, org) => (
        <JoinLeaveButton ShortCode={ShortCode} joined={org.joined} refetch={refetchUserOrgs} />
      ),
      header: () => (
        <TableSort
          currentSort={currentSort}
          sortProp={3}
          onSort={onSort}
          headerTitle="Join/leave"
        />
      ),
    },
    {
      accessor: '_',
      align: 'right',
      headerStyle: { width: '50px' },
      cell: (_, org) => (
        <OptionsMenu>
          <Menu.Item
            onClick={() =>
              showModal(
                <AssignCreatorModal name={org.name || org.Name} ShortCode={org.ShortCode} />,
              )
            }
          >
            Reassign owner
          </Menu.Item>
          <Menu.Item
            disabled={org.pendingDeletion}
            onClick={() =>
              showModal(
                <DeleteModal
                  name={org.name || org.Name}
                  ShortCode={org.ShortCode}
                  deleted={org.pendingDeletion}
                  refetch={refetchAllOrgs}
                />,
              )
            }
          >
            <Text color="Red" trim={false}>
              {org.pendingDeletion ? 'Deletion pending' : 'Delete organisation'}
            </Text>
          </Menu.Item>
        </OptionsMenu>
      ),
    },
  ];

  const pagination = usePagination({
    data: organisations,
    totalSize: organisations.length,
    page,
    pageSize: 10,
    onPageChange: p => dispatch({ type: 'SET_PAGE', payload: p }),
  });

  return (
    <Stack gap="medium">
      <Input
        icon="search"
        placeholder="Search organisations"
        value={search}
        onChange={e => dispatch({ type: 'SET_SEARCH', payload: e.target.value })}
        width="100%"
      />
      <Card p="small">
        <Stack>
          <Shelf justifyContent="space-between">
            <Text color="Mid Neutral 2">
              {organisations.length} {pluralize('organisation', organisations.length)}
            </Text>
            <Pagination pagination={pagination} />
          </Shelf>
          <Table
            noDataIndication="No organisations"
            keyField="ShortCode"
            data={organisations}
            columns={columns}
            pagination={pagination}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

// eslint-disable-next-line react/prop-types
const JoinLeaveButton = ({ joined, ShortCode, refetch }) => {
  const { execute, loading } = usePush(async () => {
    const fn = joined ? AdminService.leaveOrganisation : AdminService.joinOrganisation;
    await fn(ShortCode);
    await refetch();
  });

  return (
    <Button
      size="narrow"
      loading={loading}
      type="button"
      intent={joined ? 'muted' : 'info'}
      onClick={execute}
      style={{ width: '100%' }}
    >
      {joined ? 'Leave' : 'Join'}
    </Button>
  );
};

// eslint-disable-next-line react/prop-types
const DeleteModal = ({ ShortCode, refetch, name, open, close, onClosed }) => {
  const { execute, loading } = usePush(
    async () => {
      await AdminService.deleteOrganisation(ShortCode);
      await refetch();
    },
    {
      successToast: 'Organisation will be deleted within 72 hours',
      errorToast: true,
    },
  );

  return (
    <Formik
      initialValues={{
        confirmShortCode: '',
      }}
      validate={values => {
        if (values.confirmShortCode !== ShortCode) {
          return {
            confirmShortCode: 'Short Code is incorrect.',
          };
        }
      }}
      onSubmit={async (values, actions) => {
        const [err] = await execute(ShortCode, values);
        actions.setSubmitting(false);
        if (err) return;
        close();
      }}
      validateOnMount
    >
      {form => (
        <Modal open={open} toggle={close} title="Are you sure?" size="small" onClosed={onClosed}>
          <Form>
            <Modal.Body>
              <Stack gap="medium">
                <Text>
                  You're about to permanently delete <Text fontWeight="bold">{name}</Text>. Before
                  deleting, make sure the organisation has no active subscription. <br />
                  Please type the organisation's Short Code{' '}
                  <Text fontWeight="bold">{ShortCode}</Text> below to confirm.
                </Text>
                <FieldWithErrorLabel label="Short Code" name="confirmShortCode" as={Input} />
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <ButtonGroup>
                <Button onClick={close} appearance="secondary">
                  Cancel
                </Button>
                <Button type="submit" intent="danger" loading={loading} disabled={!form.isValid}>
                  Delete
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

// eslint-disable-next-line react/prop-types
const AssignCreatorModal = ({ ShortCode, name, open, close, onClosed }) => {
  const { data, refetch } = useFetch(SettingsService.getUsers, ShortCode);
  const { execute, loading } = usePush(
    async id => {
      await AdminService.assignOwnerOfOrganisation(ShortCode, id);
      refetch();
      close();
    },
    {
      successToast: 'Owner successfully reassigned',
      errorToast: true,
    },
  );

  const [id, setId] = useState('');
  const assignable = data.chaserUsers.filter(user => user._id !== data.createdBy._id);

  return (
    <Modal open={open} toggle={close} title="Reassign owner" size="small" onClosed={onClosed}>
      <Modal.Body>
        <Stack gap="medium">
          <Text>
            Chooose which user to assign ownership of <Text fontWeight="bold">{name}</Text> to.
          </Text>
          <ChaserSelect
            height="small"
            disabled={assignable.length === 0}
            value={id}
            onChange={e => {
              const { value } = e.target;
              setId(value);
            }}
          >
            <option value="" disabled>
              {assignable.length === 0 ? 'No users available' : 'Select a user'}
            </option>
            <option disabled>{userToLabel(data.createdBy)} (current owner)</option>
            {assignable.map(user => (
              <option value={user._id} key={user._id}>
                {userToLabel(user)}
              </option>
            ))}
          </ChaserSelect>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={close} appearance="secondary">
            Cancel
          </Button>
          <Button onClick={() => execute(id)} loading={loading} disabled={!id}>
            {id
              ? `Assign to ${userToShortLabel(data.chaserUsers.find(u => u._id === id))}`
              : 'Select a user'}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

function userToLabel(user) {
  return `${user.profile.firstName}${user.profile.lastName ? ` ${user.profile.lastName}` : ''} <${
    user.emails[0].address
  }>`;
}

function userToShortLabel(user) {
  return `${user.profile.firstName}${user.profile.lastName ? ` ${user.profile.lastName}` : ''}`;
}

export default Organisations;
